import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Building2, Eye, EyeOff, MessageSquare, ChevronDown } from 'lucide-react';
import axios from 'axios';
import PasswordRequirements, { validatePassword } from '../components/PasswordRequirements';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import useForceLightTheme from '../hooks/useForceLightTheme';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

// Country-specific data
const COUNTRIES = [
  { value: 'US', label: 'United States', states: [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ]},
  { value: 'CA', label: 'Canada', states: [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ]},
  { value: 'MX', label: 'Mexico', states: [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
    'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'México',
    'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro',
    'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
    'Veracruz', 'Yucatán', 'Zacatecas'
  ]},
  // Europe launch (Aug 2026): UK + Nordics + Eurozone markets
  { value: 'GB', label: 'United Kingdom', states: [] },
  { value: 'IE', label: 'Ireland', states: [] },
  { value: 'ES', label: 'Spain', states: [] },
  { value: 'PT', label: 'Portugal', states: [] },
  { value: 'FR', label: 'France', states: [] },
  { value: 'DE', label: 'Germany', states: [] },
  { value: 'IT', label: 'Italy', states: [] },
  { value: 'NL', label: 'Netherlands', states: [] },
  { value: 'BE', label: 'Belgium', states: [] },
  { value: 'AT', label: 'Austria', states: [] },
  { value: 'LU', label: 'Luxembourg', states: [] },
  { value: 'GR', label: 'Greece', states: [] },
  { value: 'DK', label: 'Denmark', states: [] },
  { value: 'SE', label: 'Sweden', states: [] },
  { value: 'NO', label: 'Norway', states: [] },
  { value: 'FI', label: 'Finland', states: [] },
  { value: 'OTHER', label: 'Other', states: [] }
];

// Industries served — used by Platform HQ analytics + onboarding categorization
const INDUSTRIES = [
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'construction', label: 'Construction' },
  { value: 'facilities_mgmt', label: 'Facilities Management' },
  { value: 'property_mgmt', label: 'Property Management' },
  { value: 'moving', label: 'Moving' },
  { value: 'security', label: 'Security' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' },
];

// Timezone choices — kept identical to what's offered in ManageUsers so the
// super-user lands on the same list when they later add coworkers.
const TIMEZONES = [
  // United States
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  // Canada
  { value: 'America/Toronto', label: 'Eastern Time (Canada)' },
  { value: 'America/Winnipeg', label: 'Central Time (Canada)' },
  { value: 'America/Edmonton', label: 'Mountain Time (Canada)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Canada)' },
  { value: 'America/Halifax', label: 'Atlantic Time (Canada)' },
  { value: 'America/St_Johns', label: "Newfoundland Time" },
  // Mexico
  { value: 'America/Mexico_City', label: 'Mexico City Time' },
  // Europe (Aug 2026 launch)
  { value: 'Europe/London', label: 'UK - London (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Ireland - Dublin' },
  { value: 'Europe/Lisbon', label: 'Portugal - Lisbon' },
  { value: 'Europe/Madrid', label: 'Spain - Madrid' },
  { value: 'Europe/Paris', label: 'France - Paris' },
  { value: 'Europe/Berlin', label: 'Germany - Berlin' },
  { value: 'Europe/Rome', label: 'Italy - Rome' },
  { value: 'Europe/Amsterdam', label: 'Netherlands - Amsterdam' },
  { value: 'Europe/Brussels', label: 'Belgium - Brussels' },
  { value: 'Europe/Vienna', label: 'Austria - Vienna' },
  { value: 'Europe/Luxembourg', label: 'Luxembourg' },
  { value: 'Europe/Athens', label: 'Greece - Athens' },
  { value: 'Europe/Copenhagen', label: 'Denmark - Copenhagen' },
  { value: 'Europe/Stockholm', label: 'Sweden - Stockholm' },
  { value: 'Europe/Oslo', label: 'Norway - Oslo' },
  { value: 'Europe/Helsinki', label: 'Finland - Helsinki' },
];

// Default timezone per country — applied when the admin picks their country
const COUNTRY_DEFAULT_TZ = {
  GB: 'Europe/London', IE: 'Europe/Dublin', ES: 'Europe/Madrid', PT: 'Europe/Lisbon',
  FR: 'Europe/Paris', DE: 'Europe/Berlin', IT: 'Europe/Rome', NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels', AT: 'Europe/Vienna', LU: 'Europe/Luxembourg', GR: 'Europe/Athens',
  DK: 'Europe/Copenhagen', SE: 'Europe/Stockholm', NO: 'Europe/Oslo', FI: 'Europe/Helsinki',
  US: 'America/New_York', CA: 'America/Toronto', MX: 'America/Mexico_City',
};

const CompanyRegister = () => {
  useForceLightTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    industry: '',
    unit: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    // Company contact (new — Feb 2026)
    company_phone: '',
    // Administrator account
    admin_name: '',
    admin_email: '',
    admin_phone: '',          // welcome SMS goes here
    billing_email: '',        // optional alt billing inbox (e.g. ap@acme.com)
    admin_password: '',
    confirmPassword: '',
    // "I will also be on the clock" — controls timezone + shift duration fields
    admin_is_worker: true,
    admin_timezone: 'America/Toronto',
    admin_shift_duration: 8.0,
    // TCPA / CASL / GDPR explicit opt-in. Required to register.
    messaging_consent: false,
  });
  const [showFullTerms, setShowFullTerms] = useState(false);

  const currentCountryData = COUNTRIES.find(c => c.value === selectedCountry);
  const stateLabel = selectedCountry === 'US' ? 'State' : selectedCountry === 'CA' ? 'Province' : 'State/Province';
  const postalCodeLabel = selectedCountry === 'US' ? 'ZIP Code' : selectedCountry === 'GB' ? 'Postcode' : 'Postal Code';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setFormData({
      ...formData,
      country: value,
      state: '', // Reset state when country changes
      // Default the admin's timezone to their country's primary zone
      admin_timezone: COUNTRY_DEFAULT_TZ[value] || formData.admin_timezone,
    });
  };

  const handleStateChange = (value) => {
    setFormData({
      ...formData,
      state: value
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate password strength
    const passwordValidation = validatePassword(formData.admin_password);
    if (!passwordValidation.isValid) {
      toast({
        title: 'Password Requirements Not Met',
        description: passwordValidation.errors[0],
        variant: 'destructive'
      });
      return;
    }
    
    if (formData.admin_password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.industry) {
      toast({
        title: 'Industry required',
        description: 'Please select your primary industry to continue.',
        variant: 'destructive'
      });
      return;
    }

    // Phone validation — both company and registrar phones are required so we
    // can send the welcome SMS and contact the business. We accept anything
    // with at least 10 digits; the SMS pipeline normalizes to E.164 server-side.
    const stripDigits = (v) => (v || '').replace(/\D/g, '');
    if (stripDigits(formData.company_phone).length < 10) {
      toast({
        title: 'Company phone required',
        description: 'Please enter a valid company phone number (at least 10 digits).',
        variant: 'destructive'
      });
      return;
    }
    if (stripDigits(formData.admin_phone).length < 10) {
      toast({
        title: 'Your mobile number is required',
        description: 'We use this to send you a welcome text and important account alerts.',
        variant: 'destructive'
      });
      return;
    }

    // Explicit TCPA / CASL / GDPR opt-in is required to register. We block
    // the submit here AND the backend rejects without it as a second guard.
    if (!formData.messaging_consent) {
      toast({
        title: 'Messaging consent required',
        description: 'Please review and accept the SMS & email communication terms to continue.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      // Build full address string
      const addressParts = [];
      if (formData.unit) addressParts.push(`Unit ${formData.unit}`);
      addressParts.push(formData.street_address);
      addressParts.push(formData.city);
      addressParts.push(formData.state);
      addressParts.push(formData.postal_code);
      addressParts.push(currentCountryData.label);
      
      const fullAddress = addressParts.filter(p => p).join(', ');

      const response = await axios.post(`${API}/company/register`, {
        company_name: formData.company_name,
        industry: formData.industry,
        address: fullAddress,
        company_phone: formData.company_phone.trim(),
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_phone: formData.admin_phone.trim(),
        billing_email: formData.billing_email.trim() || null,
        admin_password: formData.admin_password,
        admin_is_worker: formData.admin_is_worker,
        admin_timezone: formData.admin_timezone,
        admin_shift_duration: Number(formData.admin_shift_duration) || 8.0,
        messaging_consent: formData.messaging_consent,
        country: formData.country,
      });
      
      login(response.data.token, response.data.user, response.data.company);
      
      toast({
        title: 'Success',
        description: 'Company registered successfully! Redirecting to payment...',
      });
      
      // Redirect to payment for setup fee (with 30-day trial)
      setTimeout(() => {
        navigate('/setup-payment');
      }, 1500);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to register company',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-4xl shadow-2xl border-2 my-8">
        <CardHeader className="space-y-4 text-center bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-t-lg">
          <div className="flex justify-center mb-2">
            <div className="w-16 h-16 bg-[#1A2433] rounded-full flex items-center justify-center">
              <Building2 className="w-10 h-10 text-blue-600" />
            </div>
          </div>
          <div>
            <CardTitle className="text-3xl font-bold">Register Your Company</CardTitle>
            <CardDescription className="text-blue-100 text-base mt-2">
              Start using Hines Time for your organization
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-8">
          <form onSubmit={handleRegister} className="space-y-6">
            {/* Company Information Section */}
            <div className="border-b pb-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-300 mb-4">Company Information</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    placeholder="Enter company name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="h-11"
                  />
                </div>

                {/* Industry Selection — required for Platform HQ analytics */}
                <div className="space-y-2">
                  <Label htmlFor="industry">Primary Industry *</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="register-industry-select">
                      <SelectValue placeholder="Select your primary industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value} data-testid={`register-industry-${ind.value}`}>
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Helps us tailor reports, templates, and best-practice tips for your business.
                  </p>
                </div>

                {/* Country Selection */}
                <div className="space-y-2">
                  <Label htmlFor="country">Country *</Label>
                  <Select value={selectedCountry} onValueChange={handleCountryChange}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Address Fields */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-1 space-y-2">
                    <Label htmlFor="unit">Unit/Suite</Label>
                    <Input
                      id="unit"
                      name="unit"
                      type="text"
                      placeholder="123"
                      value={formData.unit}
                      onChange={handleChange}
                      className="h-11"
                    />
                  </div>
                  <div className="col-span-3 space-y-2">
                    <Label htmlFor="street_address">Street Address *</Label>
                    <Input
                      id="street_address"
                      name="street_address"
                      type="text"
                      placeholder="123 Main Street"
                      value={formData.street_address}
                      onChange={handleChange}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      placeholder="Enter city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">{stateLabel} *</Label>
                    {currentCountryData.states.length > 0 ? (
                      <Select value={formData.state} onValueChange={handleStateChange}>
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder={`Select ${stateLabel.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {currentCountryData.states.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="state"
                        name="state"
                        type="text"
                        placeholder={`Enter ${stateLabel.toLowerCase()}`}
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="h-11"
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code">{postalCodeLabel} *</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    type="text"
                    placeholder={selectedCountry === 'US' ? '12345' : selectedCountry === 'CA' ? 'A1A 1A1' : 'Enter postal code'}
                    value={formData.postal_code}
                    onChange={handleChange}
                    required
                    className="h-11"
                  />
                </div>

                {/* Company phone — used for business contact + 2FA fallback */}
                <div className="space-y-2">
                  <Label htmlFor="company_phone">Company Phone Number *</Label>
                  <Input
                    id="company_phone"
                    name="company_phone"
                    type="tel"
                    placeholder="+1 (416) 555-1234"
                    value={formData.company_phone}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="register-company-phone"
                  />
                  <p className="text-xs text-gray-500">
                    Main business line — shown on invoices and used for customer support.
                  </p>
                </div>
              </div>
            </div>

            {/* Administrator Account Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-300 mb-1">Administrator Account</h3>
              <p className="text-xs text-slate-500 mb-4">
                You'll be set up as the <strong>super user</strong> for {formData.company_name || 'your company'} —
                full access to billing, users, and platform settings.
              </p>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin_name">Your Full Name *</Label>
                    <Input
                      id="admin_name"
                      name="admin_name"
                      type="text"
                      placeholder="Jane Smith"
                      value={formData.admin_name}
                      onChange={handleChange}
                      required
                      className="h-11"
                      data-testid="register-admin-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin_phone">Your Mobile Number *</Label>
                    <Input
                      id="admin_phone"
                      name="admin_phone"
                      type="tel"
                      placeholder="+1 (416) 555-9876"
                      value={formData.admin_phone}
                      onChange={handleChange}
                      required
                      className="h-11"
                      data-testid="register-admin-phone"
                    />
                    <p className="text-xs text-gray-500">
                      We'll text you a welcome message and important account alerts.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_email">Your Email Address *</Label>
                  <Input
                    id="admin_email"
                    name="admin_email"
                    type="email"
                    placeholder="you@yourcompany.com"
                    value={formData.admin_email}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="register-admin-email"
                  />
                  <p className="text-xs text-gray-500">
                    Used to sign in. Also receives all account & billing notices unless you specify a different billing email below.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_email">
                    Billing / Accounts Payable Email <span className="text-slate-500 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="billing_email"
                    name="billing_email"
                    type="email"
                    placeholder="ap@yourcompany.com"
                    value={formData.billing_email}
                    onChange={handleChange}
                    className="h-11"
                    data-testid="register-billing-email"
                  />
                  <p className="text-xs text-gray-500">
                    If your invoices should go to a different inbox (e.g. an AP / accounts team), enter it here. Leave blank to use your personal email above.
                  </p>
                </div>

                {/* "I will also be on the clock" toggle — controls whether timezone + shift fields show. */}
                <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-700 bg-slate-800/30 p-4">
                  <div className="flex-1">
                    <Label htmlFor="admin_is_worker" className="text-sm font-semibold cursor-pointer">
                      I'll also be working on the clock
                    </Label>
                    <p className="text-xs text-slate-400 mt-1">
                      For small teams where the owner is also a field worker. Enable this and we'll let you clock in / out and accept job assignments just like any other user.
                    </p>
                  </div>
                  <Switch
                    id="admin_is_worker"
                    checked={formData.admin_is_worker}
                    onCheckedChange={(checked) => setFormData({ ...formData, admin_is_worker: checked })}
                    data-testid="register-admin-is-worker"
                  />
                </div>

                {/* Conditional timezone + shift duration fields */}
                {formData.admin_is_worker && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-1">
                    <div className="space-y-2">
                      <Label htmlFor="admin_timezone">Your Timezone *</Label>
                      <Select
                        value={formData.admin_timezone}
                        onValueChange={(value) => setFormData({ ...formData, admin_timezone: value })}
                      >
                        <SelectTrigger className="h-11" data-testid="register-admin-timezone">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {TIMEZONES.map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin_shift_duration">Work Day Hours (Auto Clock Out) *</Label>
                      <Select
                        value={(formData.admin_shift_duration ?? 8).toString()}
                        onValueChange={(value) => setFormData({ ...formData, admin_shift_duration: parseFloat(value) })}
                      >
                        <SelectTrigger className="h-11" data-testid="register-admin-shift">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="8">8 Hours</SelectItem>
                          <SelectItem value="10">10 Hours</SelectItem>
                          <SelectItem value="12">12 Hours</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        We'll auto-clock you out after this many hours for safety compliance.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin_password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="admin_password"
                        name="admin_password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create strong password"
                        value={formData.admin_password}
                        onChange={handleChange}
                        required
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <PasswordRequirements 
                      password={formData.admin_password} 
                      showRequirements={formData.admin_password.length > 0}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.admin_password !== formData.confirmPassword && (
                      <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ============================================================
               SMS & Email Communication Consent (TCPA / CASL / GDPR)
               Mirrors the post-login MessagingConsentDialog copy so the
               super-user is never double-prompted. Required to register.
               ============================================================ */}
            <div className="border border-yellow-300 dark:border-yellow-800 bg-yellow-50/60 dark:bg-yellow-950/30 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-yellow-700 dark:text-yellow-300" />
                <h3 className="text-base font-semibold text-yellow-900 dark:text-yellow-100">
                  SMS &amp; Email Communications
                </h3>
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
                Hines Time uses SMS and email to deliver job assignments, team updates, time-tracking
                reminders, safety alerts, and account/security notifications. Message frequency varies
                based on your team activity. <strong>Message &amp; data rates may apply.</strong>
              </p>
              <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
                You can opt out of SMS anytime by replying <strong>STOP</strong> to any message, or
                reply <strong>HELP</strong> for assistance. Email unsubscribe links are included in
                every email. We do <strong>not</strong> share, sell, or distribute your phone number
                or consent data to third parties for marketing.
              </p>

              {/* "View full messaging terms" — recycles the same copy users
                  see in MessagingConsentDialog so the agreement is identical
                  across registration and first-login flows. */}
              <Collapsible open={showFullTerms} onOpenChange={setShowFullTerms}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline flex items-center gap-1 mb-3"
                    data-testid="register-toggle-full-terms"
                  >
                    {showFullTerms ? 'Hide' : 'View'} full messaging terms
                    <ChevronDown className={`w-4 h-4 transition-transform ${showFullTerms ? 'rotate-180' : ''}`} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3 text-sm text-slate-700 dark:text-slate-300 bg-white/60 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-md p-4 mb-3">
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white mb-1">Message types you will receive</p>
                    <ul className="list-disc pl-5 space-y-0.5">
                      <li>Job assignment notifications</li>
                      <li>Team and schedule updates</li>
                      <li>Time tracking reminders (wellness checks, auto-timeout)</li>
                      <li>Safety and compliance alerts</li>
                      <li>Account and security notifications (password resets, 2FA)</li>
                      <li>System updates and important announcements</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white mb-1">How to opt out</p>
                    <p>
                      Text <strong>STOP</strong> to any SMS message at any time — you'll receive a one-time
                      confirmation, then no further SMS. Reply <strong>HELP</strong> to any message for
                      support, or email <a className="text-blue-700 dark:text-blue-300 underline" href="mailto:support@hinestime.com">support@hinestime.com</a>.
                      Opting out of SMS may delay receipt of time-sensitive job notifications. You can
                      re-enable SMS anytime in Settings.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white mb-1">Carrier &amp; data rates</p>
                    <p>
                      Standard messaging rates from your mobile carrier apply to every SMS sent and received.
                      Carriers are not liable for delayed or undelivered messages.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white mb-1">Privacy &amp; data protection</p>
                    <p>
                      Your phone number and messaging consent information are used solely for Hines Time
                      platform communications. We do <strong>not</strong> share, sell, or distribute your
                      SMS opt-in data or consent to any third parties for marketing purposes.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white mb-1">By checking the box below you</p>
                    <ul className="list-disc pl-5 space-y-0.5">
                      <li>Acknowledge you have read and understand these messaging terms</li>
                      <li>Consent to receive SMS text messages and emails from Hines Time</li>
                      <li>Understand message and data rates may apply</li>
                      <li>Understand you can opt out at any time by texting "STOP"</li>
                      <li>Confirm the phone number and email on your account are correct</li>
                    </ul>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <div className="flex items-start gap-3 bg-white dark:bg-slate-900 border-2 border-yellow-400 dark:border-yellow-700 rounded-md p-4">
                <Checkbox
                  id="messaging_consent"
                  checked={formData.messaging_consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, messaging_consent: !!checked })}
                  className="mt-1"
                  data-testid="register-messaging-consent"
                />
                <Label
                  htmlFor="messaging_consent"
                  className="text-sm font-semibold text-slate-900 dark:text-yellow-100 cursor-pointer leading-snug"
                >
                  I agree to receive SMS text messages and emails from Hines Time as described above.
                  I understand that message and data rates may apply, and I can opt out at any time
                  by texting "STOP". <span className="text-red-600 dark:text-red-400">*</span>
                </Label>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold text-lg transition-all"
              disabled={isLoading}
            >
              {isLoading ? 'Registering...' : 'Register Company'}
            </Button>
            <p className="text-[11px] text-center mt-3 text-slate-400">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a> apply.
            </p>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-400">
              Already registered?{' '}
              <button
                onClick={() => navigate('/signin')}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Sign In
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyRegister;